import * as React from 'react';
import { CheckSymbol, RadioSymbol } from 'ui';
import { LoadOptions, ComplexValue } from '../../types';
import { OptionBtnNextLevel, OptionDescription, OptionSelectMark } from '../option';
import { SelectOption } from '../select-option';
import { SelectOptions } from '../select-options';
import { SelectItem, SelectType } from '../types';
import styles from './hierarchy-options.scss';
import { isMultiple } from '../select-type';
import { SelectAllSection } from '../select-all-section';
import { OptionsPaginator } from 'ui/common/select/options-paginator';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';

import type { JSX } from "react";

const defaultEmptyArray = [];

export interface HierarchyOptionsProps {
  valueItems: SelectItem[];
  showIcons: boolean;
  onSelectOption: (item: SelectItem) => void;
  onChange: (items: SelectItem[]) => void;
  returnedOptions: ComplexValue<number>[];
  breadcrumbStack: SelectItem[];
  breadcrumb: React.ReactNode;
  selectType: SelectType;
  onLoadOptions?: (loadOptions: LoadOptions) => void;
  minLevel: number;
  levelsNotAllowed?: boolean;
  leafNodeOnly: boolean;
  descriptionAllLevels: boolean;
  showLongDescription: boolean;
  allLevelsDescriptionPrefix?: string;
  categoryId?: number;
  showBackgroundColour?: boolean;
  displaySelectAll?: boolean;
  textSelectAll?: string;
  textClearAll?: string;
  textPageOf?: string;
  switchButton?: JSX.Element;
  setCategoryId: (categoryId: number) => void;
  loadOptions?: LoadOptions;
  includeDisabledCheckbox?: JSX.Element;
  includeDisabled?: boolean;
  uncheckSelectItem?: boolean;
  readOnlyItemIds?: number[];
}
export class HierarchyOptions extends React.Component<HierarchyOptionsProps> {
  private readonly numberOfLevels: number = 2;

  private hasDescendants(item: SelectItem) {
    return !!item.descendants;
  }

  private enableSelection(item: SelectItem) {
    const { minLevel, breadcrumbStack, leafNodeOnly } = this.props;

    if (leafNodeOnly && this.hasDescendants(item)) {
      return false;
    }
    return breadcrumbStack.length >= minLevel;
  }

  private onSelect = (item: SelectItem) => {
    const { onSelectOption, allLevelsDescriptionPrefix = '' } = this.props;

    if (this.enableSelection(item)) {
      onSelectOption({
        ...item,
        description: allLevelsDescriptionPrefix + item.description
      });
    }
  };

  private loadOptionsByParentId = (parentId: number, codeCategoryId?: number, optionDomainId?: number) => {
    const categoryId = this.props.categoryId ?? codeCategoryId;
    this.props.onLoadOptions({
      parentId,
      levels: this.numberOfLevels,
      ...(isDefinedIncludingZero(categoryId) && { categoryId }),
      ...(!!this.props.includeDisabledCheckbox
        && { includeDisabled: this.props.includeDisabled ?? false }),
        ...(optionDomainId !== undefined && { optionDomainId: optionDomainId })
    });
  };

  private onExpand = (item: SelectItem) => {
    this.loadOptionsByParentId(item.value as number, item?.category?.value, item.optionDomainId);
  };

  private renderItem = (item: SelectItem, selectedValues: number[]) => {
    const {
      selectType,
      showIcons,
      showLongDescription,
      showBackgroundColour,
      readOnlyItemIds
    } = this.props;

    const hasChildren = item?.descendants?.length > 0;
    const showSelectionMark = this.enableSelection(item);
    const longDescriptionValue = showLongDescription && (item?.longDescription ?? '');

    const selectSymbol = isMultiple(selectType) ? <CheckSymbol /> : <RadioSymbol />;
    const isDisabled = readOnlyItemIds?.includes(item.value as number);

    return <SelectOption
      key={`${item.value}_${item.description}`}
      selectType={selectType}
      hasChildren={hasChildren}
      longDescription={longDescriptionValue}
    >
      {showSelectionMark && <OptionSelectMark
        selectType={selectType}
        item={item}
        onSelect={this.onSelect}
        isSelected={selectedValues.indexOf(item.value as number) > -1}
        isDisabled={isDisabled}
      />}
      <OptionDescription item={item} showIcons={showIcons} selectSymbol={selectSymbol}
        hideSelection={!showSelectionMark} showBackgroundColour={showBackgroundColour} />
      {hasChildren && <OptionBtnNextLevel item={item} onClick={this.onExpand} />}
    </SelectOption>;
  };

  public render() {
    const {
      returnedOptions = defaultEmptyArray, breadcrumb, breadcrumbStack, includeDisabledCheckbox,
      displaySelectAll, textSelectAll, textClearAll, switchButton, textPageOf,
      valueItems, leafNodeOnly, allLevelsDescriptionPrefix, readOnlyItemIds, onChange
    } = this.props;

    const selectAll = <SelectAllSection
      textSelectAll={textSelectAll}
      textClearAll={textClearAll}
      isRoot={breadcrumbStack?.length === 1}
      isSearch={false}
      valueItems={valueItems}
      returnedOptions={returnedOptions}
      readOnlyItemIds={readOnlyItemIds}
      leafNodeOnly={leafNodeOnly}
      allLevelsDescriptionPrefix={allLevelsDescriptionPrefix}
      onChange={onChange} />;

    const selectedValues = !this.props.uncheckSelectItem ? this.props.valueItems.map(i => i.value as number) : [];
    const hasSelectAll = !!displaySelectAll && returnedOptions?.length > 0;
    const hasSwitchElement = !!switchButton || !!includeDisabledCheckbox;

    return (
      <div className={styles.hierarchyOptions}>
        {breadcrumb}
        <div className="row">
          <div className={!!hasSelectAll && hasSwitchElement ? 'col-6' : 'col-12'}>
            {hasSelectAll && selectAll}
          </div>
          <div className={`${hasSwitchElement && !hasSelectAll ? 'col-12' : 'col-6'} text-end`}>
            {!!switchButton && <div className={styles.selectSwitch}>{switchButton}</div>}
            {!!includeDisabledCheckbox && includeDisabledCheckbox}
          </div>
        </div>
        <OptionsPaginator options={returnedOptions} textPageOf={textPageOf} selectedValues={selectedValues}>
          {(currentOptions) =>
            <SelectOptions showLongDescriptions={this.props.showLongDescription}>
              {currentOptions.map(i => this.renderItem(i, selectedValues))}
            </SelectOptions>
          }
        </OptionsPaginator>
      </div>);
  }
}
